<template>
  <div class="insight-report">
    <el-tabs v-model="activeTab" type="card" class="insight-tabs">
      <el-tab-pane label="增长的核心障碍" name="barriers">
        <div class="barriers">
          <el-card v-for="(item, index) in form.core_barriers" :key="index" class="barrier-card" shadow="never">
            <div slot="header" class="card-header">
              <div class="index-circle">{{ index + 1 }}</div>
              <div class="header-text">{{ item.barrier_title }}</div>
            </div>

            <div class="block block--desc">
              <div class="block-title">
                <el-tag type="info" effect="plain" size="small">具体表现</el-tag>
              </div>
              <div class="block-body">{{ item.specific_manifestation }}</div>
            </div>

            <div class="block block--cause">
              <div class="block-title">
                <el-tag type="warning" effect="plain" size="small">根本原因</el-tag>
              </div>
              <div class="block-body" v-html="item.root_cause"></div>
            </div>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane label="增长杠杆与战略机会点" name="levers">
        <div class="levers">
          <el-card v-for="(group, index) in form.growth_opportunities" :key="index" class="opportunity-card" shadow="never">
            <div slot="header" class="card-header">
              <div class="opportunity-title">
                <span>机会点：{{ group.opportunity_title }}</span>
              </div>
              <div class="opportunity-summary" v-if="group.opportunity_summary">{{ group.opportunity_summary }}</div>
            </div>

            <div class="lever-list" v-for="(lever, leverIndex) in group.growth_levers" :key="leverIndex">
              <h2>
                <span class="icon el-icon-right">&ensp;</span>
                <span>{{ lever.lever_title }}</span>
              </h2>

              <!-- 目标指标 -->
              <div class="target-indicator">
                <h3>
                  <div><span class="icon el-icon-s-flag"></span> <span>目标指标</span></div>
                </h3>
                <div class="text">{{ lever.target_metrics }}</div>
              </div>

              <!-- 核心创意 -->
              <div class="core-idea">
                <h3><span class="icon el-icon-magic-stick"></span> 核心创意</h3>

                <div class="core-idea-list">
                  <div class="idea-item">
                    <div>视觉/情节</div>
                    <p>{{ lever.core_creative.visual_plot }}</p>
                  </div>

                  <div class="idea-item">
                    <div>视觉/旁白</div>
                    <p>{{ lever.core_creative.copy_narration }}</p>
                  </div>
                </div>
              </div>
            </div>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane label="A/B测试验证方案" name="abtest">
        <h2>A/B测试验证方案</h2>
        <div class="abtest">
          <el-card v-for="(test, idx) in form.ab_test_plans" :key="idx" class="abtest-card" shadow="never">
            <div slot="header" class="abtest-header">
              <div class="badge">Test #{{ String(idx + 1).padStart(2, '0') }}</div>
              <div class="header-text">{{ test.test_objective }}</div>
            </div>

            <div class="groups">
              <div class="group group--control">
                <div class="group-title">
                  <span class="group-badge">A</span>
                  <span>对照组</span>
                </div>
                <!-- <div class="group-body">{{ test.group_a_control.key_elements }}</div> -->
                <div class="group-body">{{ test.group_a_control.description }}</div>
              </div>

              <div class="group group--experiment">
                <div class="group-title">
                  <span class="group-badge group-badge--purple">B</span>
                  <span>实验组</span>
                </div>
                <!-- <div class="group-body">{{ test.group_b_experiment.key_elements }}</div> -->
                <div class="group-body">{{ test.group_b_experiment.description }}</div>
              </div>
            </div>

            <div class="metric">
              <i class="el-icon-data-analysis"></i>
              <span class="metric-label">核心成功指标</span>
              <div class="metric-text">
                <!-- <span style="margin-right: 20px" v-for="(metric, idx) in Object.keys(test.success_metrics)" :key="idx">
                  {{ metric }}：{{ Object.values(test.success_metrics)[idx] }}
                </span> -->
                <span>{{ test.success_metrics }}</span>
              </div>
            </div>
          </el-card>
        </div>

        <div class="requirements" v-if="form.execution_requirements">
          <h4>
            <span class="el-icon-info"></span>
            <span class="text">执行要求</span>
          </h4>

          <div class="items">
            <p>{{ form.execution_requirements.priority_order }}</p>
            <p>{{ form.execution_requirements.test_duration }}</p>
            <p>{{ form.execution_requirements.iteration_advice }}</p>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// 可选：接受外部数据以便与主页面联动
// eslint-disable-next-line
const props = defineProps({ form: Object })

const activeTab = ref('barriers')
</script>

<style scoped lang="scss">
.insight-report {
  .icon {
    color: #3373fd;
  }
}

.insight-tabs {
  ::v-deep .el-tabs__header {
    margin: 0 0 16px 0;
  }
}

.barriers {
  .barrier-card {
    margin-bottom: 16px;
    border: 1px solid #e6ebf5;
    border-radius: 8px;

    ::v-deep .el-card__header {
      background: #fafcff;
      padding: 12px 16px;
      border-bottom-color: #e6ebf5;
    }

    .card-header {
      display: flex;
      align-items: center;
    }

    .index-circle {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: #ecf5ff;
      color: #409eff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      margin-right: 10px;
    }

    .header-text {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }

    .block {
      margin: 12px 16px 16px 16px;
      padding: 12px;
      border-radius: 8px;

      .block-title {
        margin-bottom: 8px;
      }

      .block-body {
        line-height: 1.7;
        color: #303133;
        font-size: 14px;
      }
    }

    .block--desc {
      background: #f5f7ff;
      border: 1px solid #d9e7ff;
    }

    .block--cause {
      background: #fff7e6;
      border: 1px solid #ffd591;
    }
  }
}

.levers {
  .opportunity-card {
    margin-bottom: 16px;
    border: 1px solid #e6ebf5;
    border-radius: 8px;

    ::v-deep .el-card__header {
      background: #f7fbff;
      padding: 12px 16px;
      border-bottom-color: #e6ebf5;
    }

    .opportunity-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      display: flex;
      align-items: center;

      .el-icon-flag {
        color: #409eff;
        margin-right: 6px;
      }
    }

    .opportunity-summary {
      margin-top: 6px;
      color: #606266;
      line-height: 1.6;
    }

    .lever-list {
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.07);
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 15px;

      .target-indicator {
        margin-top: 16px;
        padding: 20px;
        background-color: #f0f6ff;
        border-radius: 4px;
        h3 {
          color: #3373fd;
        }

        .text {
          font-weight: 700;
          font-size: 16px;
          line-height: 24px;
        }
      }

      .core-idea {
        margin-top: 20px;

        .core-idea-list {
          margin-top: 18px;
          .idea-item {
            padding: 12px;
            background-color: #f9fafc;
            border-radius: 4px;
            margin-bottom: 10px;
          }
        }
      }
    }
  }
}

.highlight {
  font-weight: 600;
  color: #303133;

  &.highlight--red {
    color: #ff4d4f;
  }

  &.highlight--orange {
    color: #fa8c16;
  }
}

/* ---------------- A/B测试样式 ---------------- */
.abtest {
  margin-top: 16px;
  .abtest-card {
    margin-bottom: 16px;
    border: 1px solid #e6ebf5;
    border-radius: 8px;

    ::v-deep .el-card__header {
      background: #fafcff;
      padding: 12px 16px;
      border-bottom-color: #e6ebf5;
    }

    .abtest-header {
      display: flex;
      align-items: center;
      .badge {
        background: #f0f5ff;
        color: #5a78ff;
        padding: 6px 10px;
        border-radius: 16px;
        font-weight: 600;
        margin-right: 10px;
      }
      .header-text {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }
    }

    .groups {
      display: flex;
      gap: 12px;
      padding: 10px 12px 0 12px;
      .group {
        flex: 1;
        border: 1px solid #e6ebf5;
        border-radius: 8px;
        padding: 10px 12px;
        &.group--control {
          background: #fbfbfb;
        }
        &.group--experiment {
          background: #f8f6ff;
          border-color: #e4d8ff;
        }

        .group-title {
          display: flex;
          align-items: center;
          font-weight: 600;
          color: #606266;
          margin-bottom: 8px;
          .group-badge {
            width: 22px;
            height: 22px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #e6ebf5;
            color: #606266;
            font-size: 13px;
            margin-right: 6px;
            &.group-badge--purple {
              background: #ede3ff;
              color: #6b4dfd;
            }
          }
        }

        .group-body {
          color: #303133;
          line-height: 1.7;
        }
      }
    }

    .metric {
      margin: 12px;
      padding: 12px;
      background: #f7f9ff;
      border: 1px solid #e6ebf5;
      border-radius: 8px;
      .el-icon-data-analysis {
        color: #5a78ff;
        margin-right: 6px;
      }
      .metric-label {
        font-weight: 600;
        color: #606266;
        margin-right: 8px;
      }
      .metric-text {
        color: #303133;
      }
    }
  }
}

.requirements {
  padding: 20px;
  border-radius: 10px;
  border: 2px solid #ffdc67;
  background-color: #fef7eb;
  h4 {
    font-size: 18px;
    line-height: 26px;
    color: #ce7428;
    .text {
      color: #693214;
      margin-left: 6px;
    }
  }
  .items {
    margin-top: 16px;
    p {
      line-height: 28px;
    }
  }
}
</style>
